import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import { SERVICE_URL } from '../../utils/api';
import shrinkImage from './shrinkImage';
import { setUrlParams } from './cleanerToken';
import { GuideList, GuideViewer, GuideEditor, GuideCard } from './MaintenanceGuides';

// ระบบซ่อมบำรุง -- หน้าเดียวใช้ 2 แบบ (docs/maintenance-system-plan.md ใน repo API)
//   ช่าง (role = technician): งานของฉัน / งานรอรับ → รับงาน → กำลังไป → ถึง → เสร็จ
//   แม่บ้าน: แจ้งปัญหา (ห้อง + รูป + ข้อความ) → ติดตามงานที่แจ้ง
// เรียก /api/staff/v1/maintenance (ชุดเดียวกับแอปในอนาคต) · ทุก action ส่ง client_action_id + occurred_at
//   -> กดซ้ำ/ส่งซ้ำตอนเน็ตหลุด server ไม่บันทึกซ้ำ · เวลาที่บันทึก = เวลาที่กดจริง
// เปิดจากลิงก์ในกลุ่มไลน์: ?view=maintenance&ticket=<id> (ไม่มี t -> ใช้รหัสที่เคยเปิดลิงก์ส่วนตัวไว้)

const STATUS = {
  triage: { label: 'รอแอดมินดู', color: '#B26B0E' },
  assigned: { label: 'รอช่างรับงาน', color: '#B26B0E' },
  accepted: { label: 'ช่างรับงานแล้ว', color: '#2C6DAF' },
  on_the_way: { label: 'ช่างกำลังไป', color: '#2C6DAF' },
  in_progress: { label: 'กำลังซ่อม', color: '#2C6DAF' },
  on_hold: { label: 'รออะไหล่/นัดใหม่', color: '#8A938B' },
  resolved: { label: 'ซ่อมเสร็จ รอตรวจ', color: '#0F8A56' },
  verified: { label: 'ตรวจรับแล้ว', color: '#0F8A56' },
  closed: { label: 'เรียบร้อย', color: '#0F8A56' },
  cancelled: { label: 'ยกเลิก', color: '#8A938B' },
};
const statusOf = (s) => STATUS[s] || { label: s, color: '#8A938B' };
const PRIORITY_COLOR = { urgent: '#CD3E33', high: '#B26B0E', normal: '#2C6DAF', low: '#8A938B' };
const ROOT_CAUSES = [
  { v: 'wear', label: 'เสื่อมตามอายุ' },
  { v: 'misuse', label: 'ใช้งานผิดวิธี' },
  { v: 'guest_damage', label: 'ลูกค้าทำเสียหาย' },
  { v: 'install', label: 'ติดตั้งไม่ดี' },
  { v: 'other', label: 'อื่นๆ' },
];
const MAX_MEDIA = 5;
const MAX_VIDEO_MB = 100;

const place = (t) => t?.room?.name_th || t?.location_text || 'ไม่ระบุจุด';
const nameOf = (c) => (c ? [c.firstname, c.lastname].filter(Boolean).join(' ') : '');
const minutesText = (min) => {
  if (min === null || min === undefined) return '-';
  if (min < 60) return `${min} นาที`;
  const h = Math.floor(min / 60);
  return min % 60 ? `${h} ชม. ${min % 60} นาที` : `${h} ชม.`;
};
const newActionId = () => {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch (e) { /* ignore */ }
  // LINE เครื่องเก่าไม่มี randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  });
};
const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
  );
});

export default function MaintenanceWork({ token, S, onBack }) {
  const initialTicket = new URLSearchParams(window.location.search).get('ticket');
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(null);          // mine | open | team | reported | report
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);        // arrive | hold | resolve | cannot | decline | info
  const [f, setF] = useState({});
  const [media, setMedia] = useState([]);        // [{ url, media_type }]
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomOpen, setRoomOpen] = useState([]);  // ใบงานค้างของห้องที่เลือก (กันแจ้งซ้ำ)
  const [link, setLink] = useState(null);
  const [roomGuides, setRoomGuides] = useState([]);   // คู่มือของห้องที่เลือกในฟอร์มแจ้งปัญหา
  const [guideOpen, setGuideOpen] = useState(null);   // { id, roomId, fromReport }
  const [guideEditing, setGuideEditing] = useState(false);
  const [, setTick] = useState(0);
  const actionIds = useRef({});

  const mt = useCallback((path, opts = {}) => axios({
    baseURL: SERVICE_URL,
    url: `staff/v1/maintenance/${path}`,
    headers: { 'x-staff-token': token || '' },
    ...opts,
  }), [token]);

  const fail = (e) => { setBusy(false); alert(e?.response?.data?.message || 'ไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองอีกครั้ง'); };

  const loadList = useCallback((scope) => {
    setList(null);
    mt(`tickets?scope=${scope}`).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, [mt]);

  const openTicket = useCallback((id) => {
    mt(`tickets/${id}`).then((r) => {
      setDetail(r.data); setForm(null); setMedia([]); setUrlParams({ ticket: id });
    }).catch((e) => { setUrlParams({ ticket: null }); alert(e?.response?.data?.message || 'เปิดใบงานไม่ได้'); });
  }, [mt]);

  useEffect(() => {
    mt('me').then((r) => {
      setMe(r.data);
      const first = r.data.is_technician ? 'mine' : 'report';
      setTab(first);
      if (first !== 'report') loadList(first);
      if (initialTicket) openTicket(initialTicket);
      if (!r.data.is_technician) mt('rooms').then((x) => setRooms(x.data || [])).catch(() => {});
    }).catch(() => setError('ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาเปิดลิงก์ส่วนตัวจากไลน์อีกครั้ง'));
    // eslint-disable-next-line
  }, []);

  // นาฬิกากำลังซ่อม
  useEffect(() => {
    if (detail?.status !== 'in_progress') return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [detail?.status]);

  const switchTab = (k) => {
    setTab(k); setDetail(null); setForm(null); setGuideOpen(null); setUrlParams({ ticket: null });
    if (k === 'report') { setF({ room_id: '', description: '' }); setMedia([]); setRoomOpen([]); setRoomGuides([]); }
    else if (k !== 'guides') loadList(k);
  };
  const backToList = () => { setDetail(null); setForm(null); setUrlParams({ ticket: null }); if (tab && !['report', 'guides'].includes(tab)) loadList(tab); };

  // ---------- อัปโหลดรูป/วิดีโอ ----------
  const onPickFiles = async (fileList) => {
    const files = [...(fileList || [])].slice(0, MAX_MEDIA - media.length);
    for (const file of files) {
      const isVideo = /^video\//.test(file.type || '');
      if (isVideo && file.size > MAX_VIDEO_MB * 1024 * 1024) { alert(`วิดีโอใหญ่เกิน ${MAX_VIDEO_MB}MB — ถ่ายสั้นลงหน่อยนะคะ`); continue; }
      setUploading((n) => n + 1);
      try {
        const body = isVideo ? file : await shrinkImage(file);
        const fd = new FormData();
        fd.append('file', body, isVideo ? (file.name || 'clip.mp4') : 'photo.jpg');
        const r = await mt('upload', { method: 'post', data: fd, timeout: 180000 });
        setMedia((m) => [...m, { url: r.data.url, media_type: r.data.media_type }]);
      } catch (e) { alert('ส่งไฟล์ไม่สำเร็จ — ลองใหม่อีกครั้ง'); }
      setUploading((n) => n - 1);
    }
  };
  const mediaPicker = (label = '📷 ถ่ายรูป / เลือกรูป-วิดีโอ', accept = 'image/*,video/*') => (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {media.map((m, i) => (
          <div key={m.url} style={{ position: 'relative' }}>
            {m.media_type === 'video'
              ? <video src={m.url} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10, background: '#000' }} />
              : <img src={m.url} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10 }} />}
            <button onClick={() => setMedia(media.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, border: 'none', borderRadius: 99, width: 24, height: 24, background: '#CD3E33', color: '#fff', fontWeight: 800 }}>×</button>
          </div>
        ))}
        {uploading > 0 && <div style={{ width: 84, height: 84, borderRadius: 10, background: '#EEF2EF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#586159' }}>กำลังส่ง…</div>}
      </div>
      {media.length < MAX_MEDIA && (
        <label style={{ ...S.back, display: 'block', textAlign: 'center', marginTop: 8, padding: 12, color: '#2C6DAF' }}>
          {label}
          <input type="file" accept={accept} multiple style={{ display: 'none' }} onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );

  // ---------- action ----------
  const act = (action, data = {}, confirmText = null) => {
    if (busy) return;
    if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
    if (confirmText && !window.confirm(confirmText)) return;
    const key = `${detail.id}:${action}`;
    if (!actionIds.current[key]) actionIds.current[key] = newActionId(); // ส่งซ้ำ (เน็ตหลุด) ใช้ id เดิม
    setBusy(true);
    mt(`tickets/${detail.id}/${action}`, {
      method: 'post',
      data: { ...data, client_action_id: actionIds.current[key], occurred_at: new Date().toISOString() },
      timeout: 60000,
    }).then((r) => {
      delete actionIds.current[key];
      setBusy(false); setForm(null); setMedia([]);
      setDetail((d) => ({ ...d, ...r.data }));
      openTicket(detail.id);
    }).catch((e) => {
      // server ตอบกลับมาแล้ว (ไม่ใช่เน็ตหลุด) -> action นี้จบ ครั้งหน้าใช้ id ใหม่
      if (e?.response) delete actionIds.current[key];
      fail(e);
    });
  };

  const arrive = async () => {
    setBusy(true);
    const pos = await getPosition();
    setBusy(false);
    act('arrive', { ...(pos || {}), photo_url: media.find((m) => m.media_type === 'image')?.url || null });
  };

  const submitReport = () => {
    if (busy) return;
    if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
    if (!f.room_id) { alert('เลือกห้องก่อนนะคะ'); return; }
    if (!media.length) { alert('ถ่ายรูปจุดที่เสียอย่างน้อย 1 รูปนะคะ'); return; }
    if (!actionIds.current.report) actionIds.current.report = newActionId();
    setBusy(true);
    mt('report', {
      method: 'post',
      data: { room_id: f.room_id, description: f.description, media, client_action_id: actionIds.current.report, occurred_at: new Date().toISOString() },
      timeout: 60000,
    }).then((r) => {
      delete actionIds.current.report;
      setBusy(false);
      alert(`ส่งเรื่องแล้ว ✓ (${r.data.code}) แอดมินจะส่งช่างให้`);
      setMedia([]); setF({ room_id: '', description: '' }); setRoomOpen([]);
      setTab('reported'); loadList('reported');
    }).catch((e) => { if (e?.response) delete actionIds.current.report; fail(e); });
  };

  const addInfo = (ticketId) => {
    if (busy) return;
    if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
    if (!f.note && !media.length) { alert('ใส่ข้อความหรือรูป'); return; }
    setBusy(true);
    mt(`tickets/${ticketId}/add-info`, { method: 'post', data: { note: f.note, media, client_action_id: newActionId(), occurred_at: new Date().toISOString() } })
      .then(() => { setBusy(false); setMedia([]); setF((x) => ({ ...x, note: '' })); setForm(null); alert('เพิ่มข้อมูลแล้ว ✓'); if (detail?.id === ticketId) openTicket(ticketId); })
      .catch(fail);
  };

  const onPickRoom = (roomId) => {
    setF((x) => ({ ...x, room_id: roomId }));
    setRoomOpen([]);
    setRoomGuides([]);
    if (roomId) {
      mt(`open?room_id=${roomId}`).then((r) => setRoomOpen(r.data || [])).catch(() => {});
      mt(`guides?room_id=${roomId}`).then((r) => setRoomGuides(r.data || [])).catch(() => {});
    }
  };

  const startLink = () => {
    mt('link-line', { method: 'post' }).then((r) => setLink(r.data)).catch(fail);
  };

  const input = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 16 };
  const tabBtn = (on) => ({ flex: 1, border: 'none', borderRadius: 10, padding: '10px 4px', fontWeight: 800, fontSize: 14, background: on ? '#0F8A56' : '#EEF2EF', color: on ? '#fff' : '#586159' });

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div><p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!me) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;
  const isTech = me.is_technician;

  // ================= คู่มือ =================
  if (guideOpen) {
    const onFeedback = (helped) => {
      const fromReport = guideOpen.fromReport;
      setGuideOpen(null);
      if (fromReport && helped) {
        alert('เยี่ยมเลย ✓ ไม่ต้องแจ้งช่าง');
        setF({ room_id: '', description: '' }); setMedia([]); setRoomOpen([]); setRoomGuides([]);
      }
      // ยังไม่หาย -> กลับฟอร์มแจ้งปัญหา (ห้องที่เลือกไว้ยังอยู่) ถ่ายรูปแล้วส่งได้เลย
    };
    return <GuideViewer mt={mt} S={S} guideId={guideOpen.id} roomId={guideOpen.roomId} askHelped={guideOpen.fromReport}
      onBack={() => setGuideOpen(null)} onFeedback={onFeedback} />;
  }
  if (guideEditing && detail) {
    return <GuideEditor mt={mt} S={S} ticket={detail} onCancel={() => setGuideEditing(false)}
      onDone={() => { setGuideEditing(false); alert('ส่งคู่มือให้แอดมินตรวจแล้ว ✓ ขอบคุณที่ช่วยสอนแม่บ้าน'); }} />;
  }

  // ================= DETAIL =================
  if (detail) {
    const st = statusOf(detail.status);
    const openVisit = (detail.visits || []).find((v) => !v.ended_at);
    const working = openVisit ? Math.round((Date.now() - new Date(openVisit.started_at)) / 60000) : null;
    const mine = detail.is_mine;
    const photos = detail.media || [];
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={backToList}>‹ รายการ</button>
          <div style={{ minWidth: 0 }}><h1 style={{ ...S.h, fontSize: 17 }}>{detail.code} · {place(detail)}</h1><div style={S.sub}>{detail.hotel?.name_th}</div></div>
        </div>

        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={S.pill(st.color)}>{st.label}</span>
            {detail.status !== 'triage' && <span style={S.pill(PRIORITY_COLOR[detail.priority] || '#2C6DAF')}>{detail.priority_label}</span>}
            {detail.category && <span style={{ fontSize: 13, color: '#586159' }}>{detail.category.icon} {detail.category.name}</span>}
          </div>
          {detail.title && <div style={{ fontSize: 18, fontWeight: 800, marginTop: 10 }}>{detail.title}</div>}
          {detail.description && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{detail.description}</div>}
          <div style={{ fontSize: 13, color: '#586159', marginTop: 8 }}>
            แจ้ง {moment(detail.reported_at).format('D MMM HH:mm')}
            {detail.assignee ? ` · ช่าง ${nameOf(detail.assignee)}` : ''}
            {detail.scheduled_at ? ` · นัด ${moment(detail.scheduled_at).format('D MMM HH:mm')}` : ''}
          </div>
          {photos.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {photos.map((m) => (m.media_type === 'video'
                ? <video key={m.id} src={m.url} controls style={{ width: '100%', borderRadius: 10 }} />
                : <a key={m.id} href={m.url} target="_blank" rel="noreferrer"><img src={m.url} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10 }} /></a>))}
            </div>
          )}
          {detail.resolution_note && <div style={{ marginTop: 10, fontSize: 14 }}>🔧 {detail.resolution_note}</div>}
        </div>

        {/* ---------- ปุ่มช่าง ---------- */}
        {isTech && detail.can_accept && !form && (
          <>
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => act('accept', {}, `รับงาน ${detail.code}?`)}>✋ รับงานนี้</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: '#8A938B' }} onClick={() => { setF({ note: '' }); setForm('decline'); }}>ไม่สะดวกรับ</button>
          </>
        )}
        {isTech && mine && ['accepted', 'on_hold'].includes(detail.status) && !form && (
          <>
            {detail.status === 'on_hold' && <div style={{ ...S.card, background: '#F7F5EE', fontSize: 14 }}>⏸ พักงานไว้ — กลับมาซ่อมต่อเมื่อพร้อม</div>}
            <button disabled={busy} style={S.bigBtn('#2C6DAF')} onClick={() => act('on-the-way')}>🚗 กำลังไป</button>
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => { setMedia([]); setForm('arrive'); }}>📍 ถึงหน้างานแล้ว</button>
          </>
        )}
        {isTech && mine && detail.status === 'on_the_way' && !form && (
          <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => { setMedia([]); setForm('arrive'); }}>📍 ถึงหน้างานแล้ว</button>
        )}
        {isTech && mine && detail.status === 'in_progress' && !form && (
          <>
            <div style={{ ...S.card, textAlign: 'center', background: '#F1F7F3' }}>
              <div style={{ fontSize: 13, color: '#586159' }}>กำลังซ่อม (เริ่ม {moment(openVisit?.started_at).format('HH:mm')})</div>
              <div style={{ fontSize: 30, fontWeight: 800 }}>{minutesText(working)}</div>
            </div>
            <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => { setMedia([]); setF({ note: '', root_cause: '' }); setForm('resolve'); }}>✅ ซ่อมเสร็จแล้ว</button>
            <button disabled={busy} style={S.bigBtn('#8A938B')} onClick={() => { setMedia([]); setF({ result: 'need_parts', note: '', expected_at: '' }); setForm('hold'); }}>⏸ รออะไหล่ / ต้องมาใหม่</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10, padding: 12, color: '#CD3E33' }} onClick={() => { setF({ note: '' }); setForm('cannot'); }}>⚠️ ซ่อมไม่ได้ / ต้องใช้ช่างอื่น</button>
          </>
        )}

        {/* ---------- ฟอร์ม ---------- */}
        {form === 'decline' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>ไม่สะดวกรับงานนี้</div>
            <input style={{ ...input, marginTop: 8 }} placeholder="เหตุผล (ถ้ามี)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <button disabled={busy} style={S.bigBtn('#8A938B')} onClick={() => act('decline', { note: f.note })}>ยืนยัน</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {form === 'arrive' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>📍 ถึงหน้างาน</div>
            <div style={{ fontSize: 13, color: '#586159' }}>ระบบเริ่มนับเวลาซ่อมตั้งแต่ตอนกด · ถ่ายรูปจุดที่เสียก่อนซ่อม (ไม่บังคับ)</div>
            {mediaPicker('📷 ถ่ายรูปก่อนซ่อม', 'image/*')}
            <button disabled={busy || uploading > 0} style={S.bigBtn('#0F8A56')} onClick={arrive}>{busy ? 'กำลังส่ง…' : 'ยืนยันถึงแล้ว'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {form === 'resolve' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>✅ ซ่อมเสร็จ</div>
            <textarea rows={3} style={{ ...input, marginTop: 8 }} placeholder="ทำอะไรไปบ้าง เช่น เปลี่ยนหลอดไฟ / ล้างแอร์" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <div style={{ fontWeight: 700, marginTop: 10, fontSize: 14 }}>สาเหตุ</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {ROOT_CAUSES.map((c) => (
                <button key={c.v} onClick={() => setF({ ...f, root_cause: f.root_cause === c.v ? '' : c.v })}
                  style={{ border: '1.5px solid #CBD4CC', borderRadius: 99, padding: '6px 12px', fontSize: 14, fontWeight: 700, background: f.root_cause === c.v ? '#0F8A56' : '#fff', color: f.root_cause === c.v ? '#fff' : '#586159' }}>{c.label}</button>
              ))}
            </div>
            {mediaPicker('📷 ถ่ายรูปหลังซ่อม')}
            <button disabled={busy || uploading > 0} style={S.bigBtn('#0F8A56')} onClick={() => act('resolve', { note: f.note, root_cause: f.root_cause || null, after_photos: media }, 'ยืนยันซ่อมเสร็จ?')}>{busy ? 'กำลังส่ง…' : 'ส่งงาน'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {form === 'hold' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>⏸ พักงาน</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {[['need_parts', 'รออะไหล่'], ['need_return', 'ต้องมาใหม่']].map(([v, l]) => (
                <button key={v} onClick={() => setF({ ...f, result: v })} style={tabBtn(f.result === v)}>{l}</button>
              ))}
            </div>
            <input style={{ ...input, marginTop: 8 }} placeholder="รายละเอียด เช่น สั่งคอมเพรสเซอร์แล้ว" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <div style={{ fontSize: 13, color: '#586159', marginTop: 8 }}>คาดว่าจะกลับมา (ไม่บังคับ)</div>
            <input type="datetime-local" style={input} value={f.expected_at} onChange={(e) => setF({ ...f, expected_at: e.target.value })} />
            <button disabled={busy} style={S.bigBtn('#8A938B')} onClick={() => act('hold', { result: f.result, note: f.note, expected_at: f.expected_at ? moment(f.expected_at).toISOString() : null, photos: media })}>{busy ? 'กำลังส่ง…' : 'ยืนยันพักงาน'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {form === 'cannot' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>⚠️ ซ่อมไม่ได้</div>
            <div style={{ fontSize: 13, color: '#586159' }}>งานจะกลับไปให้แอดมินหาช่าง/ทีมอื่น</div>
            <textarea rows={3} style={{ ...input, marginTop: 8 }} placeholder="เหตุผล (บังคับ) เช่น ต้องใช้ช่างแอร์" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <button disabled={busy || !f.note} style={S.bigBtn('#CD3E33')} onClick={() => act('cannot-fix', { note: f.note })}>ยืนยัน</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {form === 'info' && (
          <div style={S.card}>
            <div style={{ fontWeight: 800 }}>เพิ่มรูป/ข้อมูล</div>
            <textarea rows={2} style={{ ...input, marginTop: 8 }} placeholder="ข้อความ" value={f.note || ''} onChange={(e) => setF({ ...f, note: e.target.value })} />
            {mediaPicker()}
            <button disabled={busy || uploading > 0} style={S.bigBtn('#2C6DAF')} onClick={() => addInfo(detail.id)}>{busy ? 'กำลังส่ง…' : 'ส่ง'}</button>
            <button style={{ ...S.back, width: '100%', marginTop: 10 }} onClick={() => setForm(null)}>ยกเลิก</button>
          </div>
        )}
        {!form && !['closed', 'cancelled'].includes(detail.status) && (
          <button style={{ ...S.back, width: '100%', marginTop: 12, padding: 12, color: '#2C6DAF' }} onClick={() => { setMedia([]); setF({ note: '' }); setForm('info'); }}>+ เพิ่มรูป/ข้อมูล</button>
        )}
        {isTech && mine && !form && ['resolved', 'verified', 'closed'].includes(detail.status) && (
          <div style={{ ...S.card, marginTop: 12, background: '#F3EEFA', borderColor: '#DCCDF0' }}>
            <div style={{ fontWeight: 800 }}>📘 แม่บ้านแก้เองได้ไหม?</div>
            <div style={{ fontSize: 14, color: '#586159' }}>ถ้าครั้งหน้าแม่บ้านทำเองได้ (เช่น รีเซ็ตเบรกเกอร์ ล้างฟิลเตอร์) บันทึกวิธีไว้เป็นคู่มือ</div>
            <button style={{ ...S.back, width: '100%', marginTop: 8, padding: 12, color: '#7A4FB0' }} onClick={() => setGuideEditing(true)}>บันทึกเป็นคู่มือ (สอนแม่บ้าน)</button>
          </div>
        )}

        {(detail.visits || []).length > 0 && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>การเข้าหน้างาน</div>
            {detail.visits.map((v) => (
              <div key={v.id} style={{ fontSize: 14, padding: '4px 0', borderBottom: '1px solid #EEF2EF' }}>
                {moment(v.arrived_at).format('D MMM HH:mm')} – {v.ended_at ? moment(v.ended_at).format('HH:mm') : 'กำลังซ่อม'}
                {v.ended_at ? ` (${minutesText(Math.round((new Date(v.ended_at) - new Date(v.started_at)) / 60000))})` : ''}
                {v.note ? <div style={{ color: '#586159', fontSize: 13 }}>{v.note}</div> : null}
              </div>
            ))}
          </div>
        )}

        {isTech && (detail.room_history || []).length > 0 && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>เคยซ่อมห้องนี้</div>
            {detail.room_history.map((h) => (
              <div key={h.id} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px solid #EEF2EF' }}>
                <b>{moment(h.reported_at).format('D MMM YY')}</b> · {h.category ? `${h.category.icon || ''} ${h.category.name} · ` : ''}{h.title || h.description}
                {h.resolution_note && <div style={{ color: '#586159' }}>🔧 {h.resolution_note}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ================= LIST / REPORT =================
  const tabs = isTech
    ? [['mine', 'งานของฉัน'], ['open', 'งานรอรับ'], ['team', 'ทีม'], ['guides', 'คู่มือ']]
    : [['report', 'แจ้งปัญหา'], ['reported', 'ที่ฉันแจ้ง'], ['guides', 'คู่มือ']];

  return (
    <div style={S.page}>
      <div style={S.bar}>
        {onBack && <button style={S.back} onClick={onBack}>‹ กลับ</button>}
        <div><h1 style={{ ...S.h, fontSize: 18 }}>{isTech ? `🛠 สวัสดีค่ะ ช่าง${me.firstname || ''}` : '🛠 แจ้งปัญหา / ซ่อม'}</h1>
          <div style={S.sub}>{isTech ? me.teams.map((t) => t.name).join(' · ') : me.hotel?.name_th}</div></div>
      </div>

      {isTech && !me.line_linked && (
        <div style={{ ...S.card, borderColor: '#B26B0E', background: '#FFF8EE' }}>
          {!link ? (
            <>
              <div style={{ fontWeight: 800 }}>ผูกบัญชีไลน์ เพื่อกด "รับงาน" ในกลุ่มได้</div>
              <button style={{ ...S.back, width: '100%', marginTop: 8, padding: 10, color: '#B26B0E' }} onClick={startLink}>ขอรหัสผูกไลน์</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14 }}>พิมพ์ข้อความนี้ในกลุ่มไลน์ทีมช่าง (ภายใน 15 นาที)</div>
              <div style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', margin: '8px 0', letterSpacing: 2 }}>{link.message}</div>
              <button style={{ ...S.back, width: '100%' }} onClick={() => { navigator.clipboard?.writeText(link.message); }}>คัดลอก</button>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {tabs.map(([k, l]) => <button key={k} style={tabBtn(tab === k)} onClick={() => switchTab(k)}>{l}</button>)}
      </div>

      {tab === 'report' && (
        <div style={S.card}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>ห้อง</div>
          <select style={input} value={f.room_id || ''} onChange={(e) => onPickRoom(e.target.value)}>
            <option value="">— เลือกห้อง —</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name_th}</option>)}
          </select>

          {roomOpen.length > 0 && (
            <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: '#FFF8EE', border: '1px solid #F0D9B5' }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>ห้องนี้มีเรื่องที่แจ้งไว้แล้ว</div>
              <div style={{ fontSize: 13, color: '#586159' }}>ถ้าเป็นเรื่องเดียวกัน กด "เพิ่มข้อมูล" แทนแจ้งใหม่นะคะ</div>
              {roomOpen.map((t) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <div style={{ flex: 1, fontSize: 14 }}>
                    <b>{t.code}</b> {t.category ? `${t.category.icon || ''} ${t.category.name}` : ''} · {t.title || t.description}
                    <div><span style={S.pill(statusOf(t.status).color)}>{statusOf(t.status).label}</span></div>
                  </div>
                  <button style={{ ...S.back, color: '#2C6DAF' }} onClick={() => openTicket(t.id)}>เพิ่มข้อมูล</button>
                </div>
              ))}
            </div>
          )}

          {roomGuides.length > 0 && (
            <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: '#F1F7F3', border: '1px solid #CFE6DA' }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>💡 ลองแก้เองก่อนไหม?</div>
              <div style={{ fontSize: 13, color: '#586159', marginBottom: 8 }}>มีคู่มือสำหรับห้องนี้ ถ้าแก้ได้ไม่ต้องรอช่าง</div>
              {roomGuides.slice(0, 4).map((g) => <GuideCard key={g.id} g={g} S={S} onOpen={(id) => setGuideOpen({ id, roomId: f.room_id, fromReport: true })} />)}
              {roomGuides.length > 4 && <div style={{ fontSize: 13, color: '#586159' }}>และอีก {roomGuides.length - 4} คู่มือ ในแท็บ "คู่มือ"</div>}
            </div>
          )}

          <div style={{ fontWeight: 800, marginTop: 14 }}>รูปจุดที่เสีย <span style={{ color: '#CD3E33' }}>*</span></div>
          {mediaPicker()}
          <div style={{ fontWeight: 800, marginTop: 14, marginBottom: 6 }}>เกิดอะไรขึ้น</div>
          <textarea rows={3} style={input} placeholder="เช่น ไฟห้องน้ำไม่ติด / แอร์มีน้ำหยด" value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <button disabled={busy || uploading > 0} style={S.bigBtn('#CD3E33')} onClick={submitReport}>{busy ? 'กำลังส่ง…' : 'ส่งเรื่องให้แอดมิน'}</button>
          <div style={{ fontSize: 12, color: '#8A938B', marginTop: 8, textAlign: 'center' }}>ไม่ต้องเลือกประเภทหรือความด่วน แอดมินจะดูแล้วส่งช่างให้</div>
        </div>
      )}

      {tab === 'guides' && <GuideList mt={mt} S={S} onOpen={(id) => setGuideOpen({ id, roomId: null, fromReport: false })} />}

      {!['report', 'guides'].includes(tab) && list === null && <div style={{ textAlign: 'center', color: '#8A938B', padding: 20 }}>กำลังโหลด…</div>}
      {!['report', 'guides'].includes(tab) && list && list.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีรายการ</div>}
      {!['report', 'guides'].includes(tab) && (list || []).map((t) => {
        const st = statusOf(t.status);
        const thumb = (t.media || [])[0];
        return (
          <div key={t.id} style={{ ...S.card, cursor: 'pointer', borderLeft: `5px solid ${t.status === 'triage' ? '#B26B0E' : (PRIORITY_COLOR[t.priority] || '#DBE2DC')}` }} onClick={() => openTicket(t.id)}>
            <div style={{ display: 'flex', gap: 10 }}>
              {thumb && thumb.media_type === 'image' && <img src={thumb.url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800 }}>{t.code} · {place(t)}</div>
                <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.category ? `${t.category.icon || ''} ${t.category.name} · ` : ''}{t.title || t.description}</div>
                <div style={{ fontSize: 12, color: '#586159' }}>
                  {isTech ? t.hotel?.name_th : ''} · {moment(t.reported_at).fromNow()}
                  {t.assignee && !t.is_mine ? ` · ${nameOf(t.assignee)}` : ''}
                </div>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <span style={S.pill(st.color)}>{st.label}</span>
                {t.status !== 'triage' && t.priority_label && <div style={{ fontSize: 12, marginTop: 4 }}>{t.priority_label}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
